import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { Sidebar } from "./sidebar";
import { Topbar } from "./topbar";
import { Toaster } from "../ui/toaster";
import type { WebModule } from "../../modules";
import { restoreScroll, savedScroll, saveScroll, useNavigationMemory } from "../../lib/navigation";

export function Shell({ modules, children }: { modules: WebModule[]; children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigationType = useNavigationType();
  const contentRef = useRef<HTMLElement>(null);
  const previousPath = useRef(location.pathname);
  useNavigationMemory();

  // Retient la position de défilement de l'entrée affichée, au fil du défilement.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => saveScroll(location.key, el.scrollTop));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", onScroll);
    };
  }, [location.key]);

  // Ferme le tiroir mobile à chaque navigation. Un retour (précédent du
  // navigateur ou bouton « Retour ») rétablit la position de défilement ; une
  // nouvelle page s'ouvre en haut ; un simple changement de filtre ne bouge pas.
  useEffect(() => {
    setNavOpen(false);
    const el = contentRef.current;
    const pathChanged = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;
    if (!el) return;
    if (navigationType === "POP") return restoreScroll(el, savedScroll(location.key));
    if (pathChanged) el.scrollTo({ top: 0, left: 0, behavior: "instant" });
    return undefined;
  }, [location.key, location.pathname, navigationType]);

  // Bloque le défilement de fond quand le tiroir est ouvert.
  useEffect(() => {
    document.body.classList.toggle("is-nav-locked", navOpen);
    return () => document.body.classList.remove("is-nav-locked");
  }, [navOpen]);

  return (
    <div className={`shell${navOpen ? " is-nav-open" : ""}`}>
      <aside className="shell__sidebar" id="app-sidebar">
        <Sidebar modules={modules} />
      </aside>
      <div className="shell__backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      <header className="shell__topbar">
        <Topbar modules={modules} onMenu={() => setNavOpen((v) => !v)} navOpen={navOpen} />
      </header>
      <main className="shell__content" ref={contentRef}>
        {children}
      </main>
      <Toaster />
    </div>
  );
}
